
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Download } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useState, useEffect } from "react";

interface InvoicePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceData: {
    invoiceNumber: string;
    pdfBase64: string;
  } | null;
  onDownload: () => void;
}

const InvoicePreviewDialog = ({
  open,
  onOpenChange,
  invoiceData,
  onDownload,
}: InvoicePreviewDialogProps) => {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Reset loading state when dialog opens or invoice data changes
    if (open && invoiceData) {
      setIsLoading(true);
    }
  }, [open, invoiceData]);

  if (!invoiceData) {
    return null;
  }
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-4">
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center">
            <span>{t("viewInvoice")} #{invoiceData.invoiceNumber}</span>
            <Button 
              onClick={onDownload}
              variant="outline"
              className="gap-2"
            >
              <Download size={16} />
              {t("downloadInvoice")}
            </Button>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 mt-4 relative">
          {invoiceData.pdfBase64 ? (
            <iframe
              src={`data:application/pdf;base64,${invoiceData.pdfBase64}`}
              className="w-full h-[70vh]"
              title="Quotation Preview"
              onLoad={() => setIsLoading(false)}
            />
          ) : (
            <div className="flex items-center justify-center h-[70vh] bg-muted/50 dark:bg-muted/20 rounded-md">
              {t("invoiceGenerated")}
            </div>
          )}
          
          {isLoading && invoiceData.pdfBase64 && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p>{t("loading")}</p>
              </div>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicePreviewDialog;
